import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../components/AuthContext";
import { toast } from "react-toastify";
import { confirmSwal } from "../utils/confirm";

export default function MyAccount() {
  const { authToken, userData, setUserData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [emailSubscriptions, setEmailSubscriptions] = useState([]);
  
  // Profile form data
  const [profileData, setProfileData] = useState({
    first_name: "",
    surname: "",
    known_as: "",
    title: "",
    physical_address: "",
    cell_number: "",
    home_number: "",
    preferred_number: "cell",
    email_address: "",
    year_started: new Date().getFullYear()
  });

  // Password form data
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [showPassword, setShowPassword] = useState(false);

  // Load user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!authToken) {
          setLoading(false);
          return;
        }

        const res = await axios.get("/api/user/profile", {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        const user = res.data;
        setProfileData({
          first_name: user.first_name || "",
          surname: user.surname || "",
          known_as: user.known_as || "",
          title: user.title || "",
          physical_address: user.physical_address || "",
          cell_number: user.cell_number || "",
          home_number: user.home_number || "",
          preferred_number: user.preferred_number || "cell",
          email_address: user.email_address || "",
          year_started: user.year_started || new Date().getFullYear()
        });

      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Error loading profile data");
      } finally {
        setLoading(false);
      }
    };

    const fetchEmailSubscriptions = async () => {
      if (!authToken) return;
      
      setLoadingSubscriptions(true);
      try {
        const res = await axios.get("/api/user/email-subscriptions", {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setEmailSubscriptions(res.data.subscriptions || []);
      } catch (error) {
        console.error("Error fetching email subscriptions:", error);
        toast.error("Error loading email subscriptions");
      } finally {
        setLoadingSubscriptions(false);
      }
    };

    fetchProfile();
    fetchEmailSubscriptions();
  }, [authToken]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      const updateData = {
        first_name: profileData.first_name,
        surname: profileData.surname,
        known_as: profileData.known_as,
        title: profileData.title,
        physical_address: profileData.physical_address,
        cell_number: profileData.cell_number,
        home_number: profileData.home_number,
        preferred_number: profileData.preferred_number,
        email_address: profileData.email_address.toLowerCase(),
        year_started: parseInt(profileData.year_started)
      };

      const res = await axios.put("/api/user/profile", updateData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast.success("Profile updated successfully!");
      
      // Update the user data in context
      setUserData(res.data);

    } catch (error) {
      console.error("Error updating profile:", error);
      if (error.response?.data?.detail) {
        toast.error(`Error updating profile: ${error.response.data.detail}`);
      } else {
        toast.error("Error updating profile. Please try again.");
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangingPassword(true);

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      setChangingPassword(false);
      return;
    }

    // Validate password strength
    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters long");
      setChangingPassword(false);
      return;
    }

    try {
      await axios.put("/api/user/password", {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast.success("Password changed successfully!");
      
      // Clear password form
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });

    } catch (error) {
      console.error("Error changing password:", error);
      if (error.response?.data?.detail) {
        toast.error(`Error changing password: ${error.response.data.detail}`);
      } else {
        toast.error("Error changing password. Please try again.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUnsubscribe = async (listId, listName) => {
    const confirmed = await confirmSwal({
      title: "Unsubscribe from Email List?",
      text: `Are you sure you want to unsubscribe from "${listName}"?`,
      confirmText: "Yes, Unsubscribe",
      cancelText: "Cancel",
      icon: "question",
      confirmColor: "#dc3545"
    });

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/user/email-subscriptions/${listId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast.success(`Successfully unsubscribed from ${listName}`);
      
      // Remove the list from the local state
      setEmailSubscriptions(prev => prev.filter(list => list._id !== listId));

    } catch (error) {
      console.error("Error unsubscribing:", error);
      if (error.response?.data?.detail) {
        toast.error(`Error unsubscribing: ${error.response.data.detail}`);
      } else {
        toast.error("Error unsubscribing from email list. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: "#F0F0F1", minHeight: "100vh", paddingTop: "0px", fontFamily: "'Montserrat', sans-serif" }}>
        <div className="container py-5">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authToken) {
    return (
      <div style={{ backgroundColor: "#F0F0F1", minHeight: "100vh", paddingTop: "0px", fontFamily: "'Montserrat', sans-serif" }}>
        <div className="container py-5">
          <div className="text-center">
            <h2>Please log in to access your account settings</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#F0F0F1", minHeight: "100vh", paddingTop: "0px", fontFamily: "'Montserrat', sans-serif" }}>
      <div className="container py-3 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="text-center mb-4">
              <h1 className="h2">My Account</h1>
              <p className="text-muted">Welcome, {userData?.first_name} {userData?.surname}!</p>
            </div>

            {/* Email Subscriptions Card */}
            <div className="card shadow-sm rounded-0 bg-white mb-4" style={{ padding: "20px" }}>
              <h3 className="mb-4">Subscribed Email Lists</h3>
              
              {loadingSubscriptions ? (
                <div className="d-flex justify-content-center py-3">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading subscriptions...</span>
                  </div>
                </div>
              ) : emailSubscriptions.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">You are not currently subscribed to any email lists.</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {emailSubscriptions.map((subscription) => (
                    <div key={subscription._id} className="list-group-item d-flex justify-content-between align-items-start border-0 px-0">
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{subscription.group_name}</h6>
                        {subscription.description && (
                          <p className="mb-1 text-muted small">{subscription.description}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm ms-3"
                        onClick={() => handleUnsubscribe(subscription._id, subscription.group_name)}
                      >
                        Unsubscribe
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Account Details Card */}
            <div className="card shadow-sm rounded-0 bg-white mb-4" style={{ padding: "20px" }}>
              <h3 className="mb-4">Account Details</h3>
              
              <form onSubmit={handleProfileSubmit}>
                {/* Personal Information */}
                <h5 className="mb-3 primary-color">Personal Information</h5>
                
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="first_name" className="form-label">First Name *</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      className="form-control"
                      value={profileData.first_name}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="surname" className="form-label">Surname *</label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      className="form-control"
                      value={profileData.surname}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="known_as" className="form-label">Known As</label>
                    <input
                      type="text"
                      id="known_as"
                      name="known_as"
                      className="form-control"
                      value={profileData.known_as}
                      onChange={handleProfileChange}
                      placeholder="Nickname or preferred name"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="title" className="form-label">Title</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="form-control"
                      value={profileData.title}
                      onChange={handleProfileChange}
                      placeholder="Dr., Mr., Ms., etc."
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <h5 className="mb-3 primary-color mt-4">Contact Information</h5>

                <div className="mb-3">
                  <label htmlFor="physical_address" className="form-label">Physical Address</label>
                  <textarea
                    id="physical_address"
                    name="physical_address"
                    className="form-control"
                    rows="3"
                    value={profileData.physical_address}
                    onChange={handleProfileChange}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="cell_number" className="form-label">Cell Number</label>
                    <input
                      type="tel"
                      id="cell_number"
                      name="cell_number"
                      className="form-control"
                      value={profileData.cell_number}
                      onChange={handleProfileChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="home_number" className="form-label">Home Number</label>
                    <input
                      type="tel"
                      id="home_number"
                      name="home_number"
                      className="form-control"
                      value={profileData.home_number}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="preferred_number" className="form-label">Preferred Number</label>
                  <select
                    id="preferred_number"
                    name="preferred_number"
                    className="form-select"
                    value={profileData.preferred_number}
                    onChange={handleProfileChange}
                  >
                    <option value="cell">Cell</option>
                    <option value="home">Home</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="email_address" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email_address"
                    name="email_address"
                    className="form-control"
                    value={profileData.email_address}
                    onChange={handleProfileChange}
                    required
                    autoComplete="email"
                  />
                </div>

                {/* Membership Information */}
                <h5 className="mb-3 primary-color mt-4">Membership Information</h5>

                <div className="mb-4">
                  <label htmlFor="year_started" className="form-label">Year Started *</label>
                  <input
                    type="number"
                    id="year_started"
                    name="year_started"
                    className="form-control"
                    value={profileData.year_started}
                    onChange={handleProfileChange}
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-admin"
                    disabled={savingProfile}
                  >
                    {savingProfile ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving Changes...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Password Change Card */}
            <div className="card shadow-sm rounded-0 bg-white" style={{ padding: "20px" }}>
              <h3 className="mb-4">Change Password</h3>
              
              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-3">
                  <label htmlFor="current_password" className="form-label">Current Password *</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="current_password"
                      name="current_password"
                      className="form-control"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="new_password" className="form-label">New Password *</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="new_password"
                    name="new_password"
                    className="form-control"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    autoComplete="new-password"
                    minLength="6"
                  />
                  <div className="form-text">Password must be at least 6 characters long.</div>
                </div>

                <div className="mb-4">
                  <label htmlFor="confirm_password" className="form-label">Confirm New Password *</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirm_password"
                    name="confirm_password"
                    className="form-control"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-admin"
                    disabled={changingPassword}
                  >
                    {changingPassword ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Changing Password...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
